import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Button } from '@/components/Button/Button';
import { Input } from '@/components/form/fields/Input/Input';
import { PhoneInput } from '@/components/form/fields/PhoneInput/PhoneInput';
import { Notification } from '@/components/Notification/Notification';
import { ExternalLink } from '@/components/ExternalLink/ExternalLink';
import { TRUST_STAMP_URL } from '@/bundle/_Recipient/_Details/ui/RecipientDocumentVerification/helpers';
import { VerificationInProgress } from './VerificationInProgress';
import { useSecureLinkVerification } from './useSecureLinkVerification';

type PropsType = {
  organization: string;
  what: string;
  needsEmailCode: boolean;
  v: ReturnType<typeof useSecureLinkVerification>;
};

export const VerificationBlock = ({ organization, what, needsEmailCode, v }: PropsType) => (
  <>
    <Typography variant='headline4'>Verify your identity</Typography>
    <Typography variant='bodySmall'>
      <b>{organization}</b> protects its bank details. Verify once with a government ID and a short liveness check
      through <ExternalLink href={TRUST_STAMP_URL}>TrustStamp</ExternalLink>, then {what} opens.
    </Typography>
    {v.error && <Notification variant='error'>{v.error}</Notification>}

    {needsEmailCode && !v.emailChecked && (
      <Box display='flex' flexDirection='column' rowGap='12px'>
        <Typography variant='bodySmall'>First, confirm the email address this link was sent to.</Typography>
        {v.codeSent ? (
          <>
            <Input
              name='code'
              label='6-digit code'
              value={v.emailCode}
              placeholder='Enter OTP code'
              onChange={(e) => v.setEmailCode(e.target.value)}
            />
            <Button size='medium' isLoading={v.emailOtp.isPending} onClick={() => v.emailOtp.mutate(v.emailCode)}>
              Confirm code
            </Button>
          </>
        ) : (
          <Button size='medium' isLoading={v.emailOtp.isPending} onClick={() => v.emailOtp.mutate(undefined)}>
            Email me a code
          </Button>
        )}
      </Box>
    )}

    {(!needsEmailCode || v.emailChecked) && (!v.inProgress || v.showPhoneForm) && (
      <Box display='flex' flexDirection='column' rowGap='12px'>
        <PhoneInput name='mobile_phone' label='Your mobile number' value={v.phone} onChange={v.setPhone} />
        <Typography variant='caption'>
          TrustStamp will text you a link to complete the verification on your phone.
        </Typography>
        <Button size='medium' isLoading={v.start.isPending} disabled={!v.phone} onClick={() => v.start.mutate()}>
          Start verification
        </Button>
      </Box>
    )}

    {(!needsEmailCode || v.emailChecked) && v.inProgress && (
      <VerificationInProgress
        key={v.verifyStatus.session_id ?? 'session'}
        phone={v.verifyStatus.phone_masked}
        isLoading={v.resend.isPending}
        canResend={v.verifyStatus.can_resend}
        onResend={() => v.resend.mutate()}
        onChangeNumber={() => v.setShowPhoneForm(true)}
        resendIn={v.resendIn}
        onTimerEnds={v.refetchStatus}
      />
    )}
  </>
);
